import { useMemo, useCallback } from 'react';
import './styles.scss';
import { WEEKDAYS } from '../../../../../constants';
import { convertTimeRangeTo12Hour } from '../../../../../utility';
import cx from 'classnames';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import MasterScheduleTableRow from './components/MasterScheduleTableRow';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import { addUnitRoleService } from '../../../../../services/scheduling';
import { useAuth } from '../../../../../container/authContext';

const MasterScheduleTable = ({
  scheduleData,
  getScheduleData,
  roleLabels,
  selectedCells,
  setSelectedCells,
  sidebarAction,
  selectedUser,
  formData,
  showNames,
  selectedAssignmentsToDelete,
  setSelectedAssignmentsToDelete,
}) => {
  const { user = {} } = useAuth();
  const { company: { id: companyID } = {} } = user;

  const selectedUserAssignments = useMemo(() => {
    const assignments = [];

    scheduleData.units.forEach((unit) => {
      unit.roles.forEach((role) => {
        role.master_scheduling_cells.forEach((cell) => {
          if (cell.first_half_user?.user_id === selectedUser?.id) {
            assignments.push({ day: cell.day, shift: cell.shift, halfPart: 'first_half' });
          } else if (cell.second_half_user?.user_id === selectedUser?.id) {
            assignments.push({ day: cell.day, shift: cell.shift, halfPart: 'second_half' });
          } else if (cell.both_halves_user?.user_id === selectedUser?.id) {
            assignments.push({ day: cell.day, shift: cell.shift, halfPart: 'both_halves' });
          }
        });
      });
    });

    return assignments;
  }, [selectedUser, scheduleData]);

  const handleCellClick = (cell, role) => {
    if (selectedCells.find((item) => item.id === cell.id)) {
      setSelectedCells((prevState) => prevState.filter((item) => item?.id !== cell.id));
      return;
    }

    if (!selectedUser) {
      return;
    }

    if (!selectedUser?.is_nurse && cell.role === 'nurse') {
      return;
    }

    if (!selectedUser?.is_cna && cell.role === 'cna') {
      return;
    }

    if (selectedCells.find((item) => item.day === cell.day && item.shift === cell.shift)) {
      return;
    }

    if (sidebarAction === 'add') {
      if (
        (formData.halfPart.value === 'first_half' && (cell.first_half_user?.id || cell.both_halves_user?.id)) ||
        (formData.halfPart.value === 'second_half' && (cell.second_half_user?.id || cell.both_halves_user?.id)) ||
        (formData.halfPart.value === 'both_halves' && (cell.both_halves_user?.id || cell.first_half_user?.id || cell.second_half_user?.id))
      ) {
        return;
      }

      if (formData.halfPart.value === 'both_halves') {
        if (
          selectedUserAssignments.find(
            (item) =>
              item.day === cell.day &&
              item.shift === cell.shift &&
              (item.halfPart === 'first_half' || item.halfPart === 'second_half' || item.halfPart === 'both_halves'),
          )
        ) {
          return;
        }
      } else if (formData.halfPart.value === 'first_half') {
        if (
          selectedUserAssignments.find(
            (item) =>
              item.day === cell.day && item.shift === cell.shift && (item.halfPart === 'first_half' || item.halfPart === 'both_halves'),
          )
        ) {
          return;
        }
      } else if (formData.halfPart.value === 'second_half') {
        if (
          selectedUserAssignments.find(
            (item) =>
              item.day === cell.day && item.shift === cell.shift && (item.halfPart === 'second_half' || item.halfPart === 'both_halves'),
          )
        ) {
          return;
        }
      }

      setSelectedCells((prevState) => [...prevState.filter((item) => item?.id !== cell.id), cell]);
    }

    if (sidebarAction === 'delete') {
      // The half-part select only renders in add mode, so `formData.halfPart` is stale here: take
      // whichever half the selected user actually occupies. Ignoring cells filled by someone else
      // matters - the backend re-filters the ids by user and 500s when that leaves nothing.
      const cellAssignmentId = [cell.first_half_user, cell.second_half_user, cell.both_halves_user].find(
        (occupant) => occupant?.user_id === selectedUser.id,
      )?.id;

      if (cellAssignmentId) {
        if (selectedAssignmentsToDelete.find((item) => item === cellAssignmentId)) {
          setSelectedAssignmentsToDelete((prevState) => prevState.filter((item) => item !== cellAssignmentId));
        } else {
          setSelectedAssignmentsToDelete((prevState) => [...prevState.filter((item) => item !== cellAssignmentId), cellAssignmentId]);
        }
      }
    }
  };

  const handleAddUnitRole = useCallback(
    async (payload) => {
      try {
        const params = {};
        const response = await addUnitRoleService(payload, params);
        if (isResponseOk(response) && response.data) {
          getScheduleData();
        }
      } catch (e) {
        console.error(e);
      }
    },
    [companyID],
  );

  const sundayDays = useMemo(() => {
    const sundayDaysList = [];
    scheduleData.period_days.forEach((item, index) => {
      if (item === 1) {
        sundayDaysList.push(index);
      }
    });

    return sundayDaysList;
  }, [scheduleData.period_days]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="master-schedule-table-container table-container" style={selectedUser ? { width: 'calc(100% - 390px)' } : {}}>
        <table border="1" cellPadding="8" cellSpacing="0" className="master-schedule-table">
          <thead>
            <tr>
              <th colSpan="2" className="unit-info-col"></th>
              {scheduleData.period_days.map((item, index) => (
                <th
                  className={cx('date-col', { 'first-in-sunday': item === 1 && index !== 0 })}
                  colSpan={3}
                  style={
                    showNames
                      ? { minWidth: '279px', color: item === 1 || item === 7 ? '#d92d20' : '#222222' }
                      : { color: item === 1 || item === 7 ? '#d92d20' : '#222222' }
                  }
                >
                  {WEEKDAYS[item - 1]}
                </th>
              ))}
            </tr>
            <tr>
              <th colSpan="2" className="unit-info-col"></th>
              {Array.from({ length: 14 }).map((_, index) => (
                <>
                  <th
                    className={cx('shift-col-name first-in-date', { 'first-in-sunday': sundayDays.includes(index) && index !== 0 })}
                    style={showNames ? { minWidth: '93px' } : {}}
                  >
                    {convertTimeRangeTo12Hour(scheduleData.shift_1)}
                  </th>
                  <th className="shift-col-name" style={showNames ? { minWidth: '93px' } : {}}>
                    {convertTimeRangeTo12Hour(scheduleData.shift_2)}
                  </th>
                  <th className="shift-col-name" style={showNames ? { minWidth: '93px' } : {}}>
                    {convertTimeRangeTo12Hour(scheduleData.shift_3)}
                  </th>
                </>
              ))}
            </tr>
          </thead>
          <tbody>
            {scheduleData.units.map((unit, index) => {
              const orderedUnitRoles = unit.roles.slice().sort((a, b) => a.role.localeCompare(b.role));

              const nurses = orderedUnitRoles
                .filter((role) => role.role === 'nurse')
                .sort((a, b) => {
                  const nameA = a.name?.trim();
                  const nameB = b.name?.trim();

                  if (!nameA && !nameB) return 0;
                  if (!nameA) return 1;
                  if (!nameB) return -1;

                  return nameA.localeCompare(nameB);
                })
                .map((item, nurseIndex) => ({
                  ...item,
                  name: item.name || `${roleLabels?.nurse_role_label}-${nurseIndex + 1}`,
                }));

              const cnas = orderedUnitRoles
                .filter((role) => role.role === 'cna')
                .sort((a, b) => {
                  const nameA = a.name?.trim();
                  const nameB = b.name?.trim();

                  if (!nameA && !nameB) return 0;
                  if (!nameA) return 1;
                  if (!nameB) return -1;

                  return nameA.localeCompare(nameB);
                })
                .map((item, cnaIndex) => ({
                  ...item,
                  name: item.name || `${roleLabels?.cna_role_label}-${cnaIndex + 1}`,
                }));

              return (
                <>
                  <MasterScheduleTableRow
                    index={index}
                    unit={unit}
                    roleName={nurses[0].name}
                    cells={nurses[0].master_scheduling_cells}
                    showNames={showNames}
                    scheduleData={scheduleData}
                    formData={formData}
                    selectedUser={selectedUser}
                    sidebarAction={sidebarAction}
                    selectedAssignmentsToDelete={selectedAssignmentsToDelete}
                    getScheduleData={getScheduleData}
                    handleCellClick={handleCellClick}
                    selectedCells={selectedCells}
                    role={'nurse'}
                    roleLabels={roleLabels}
                    handleAddUnitRole={handleAddUnitRole}
                    isFirstNurseRole
                    sundayDays={sundayDays}
                  />
                  {nurses.slice(1).map((nurse, nurseIndex) => (
                    <MasterScheduleTableRow
                      index={index}
                      key={nurse.id}
                      unit={unit}
                      roleName={nurse.name}
                      cells={nurse.master_scheduling_cells}
                      showNames={showNames}
                      scheduleData={scheduleData}
                      formData={formData}
                      selectedUser={selectedUser}
                      sidebarAction={sidebarAction}
                      selectedAssignmentsToDelete={selectedAssignmentsToDelete}
                      getScheduleData={getScheduleData}
                      handleCellClick={handleCellClick}
                      selectedCells={selectedCells}
                      role={'nurse'}
                      handleAddUnitRole={handleAddUnitRole}
                      roleLabels={roleLabels}
                      sundayDays={sundayDays}
                    />
                  ))}
                  {cnas.map((cna, cnaIndex) => (
                    <MasterScheduleTableRow
                      index={index}
                      key={cna.id}
                      unit={unit}
                      isFirstCnaRole={cnaIndex === 0}
                      roleName={cna.name}
                      cells={cna.master_scheduling_cells}
                      showNames={showNames}
                      scheduleData={scheduleData}
                      formData={formData}
                      selectedUser={selectedUser}
                      sidebarAction={sidebarAction}
                      selectedAssignmentsToDelete={selectedAssignmentsToDelete}
                      getScheduleData={getScheduleData}
                      handleCellClick={handleCellClick}
                      selectedCells={selectedCells}
                      handleAddUnitRole={handleAddUnitRole}
                      role={'cna'}
                      roleLabels={roleLabels}
                      sundayDays={sundayDays}
                    />
                  ))}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </DndProvider>
  );
};

export default MasterScheduleTable;
